import { Box, Divider, Fab, Grid, useMediaQuery } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import styles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import clsx from "clsx";
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ChatbotContext, ChatbotContextPayload } from '../../../context/chatbotContext';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { currentActiveChatbotThreadTypeNew, sendCannedResponsePayload, sendTemplatePayload, threadMetaData, uploadedMedia } from '../../../models/axiosModel';
import agent from '../../../services/api';
import { useSearchParams } from 'react-router-dom';
import whatsAppDefaultBgImg from "../../../assets/images/chatWidgetComponents/WhatsAppDefaultBg.png";
import ThreadLogHeader from './threadLogHeader';
import ThreadMessage from './threadMessage';
import ChatInput from '../../../pages/ruleBasedInbox/threadView/components/chatInput/chatInput';
import { getVendorField } from '../../../utils/whiteLabelling';
import ThreadViewSkeleton from '../../../pages/ruleBasedInbox/threadView/ThreadViewSkeleton';
import { useRuleBasedInboxStore } from '../../../store/ruleBasedInboxStore';
import { useShallow } from 'zustand/react/shallow';
import { IntegrationContext, IntegrationContextPayload } from '../../../context/integrationContext';
import { appendPendingManualMessage, buildOptimisticCannedResponseMessages, buildOptimisticManualMessage, buildOptimisticTemplateMessages, getCannedResponseMessageIds, getTemplateMessageId, reconcilePendingManualMessage, removePendingManualMessage } from '../../../pages/ruleBasedInbox/threadView/components/pendingManualMessageUtils';
import { getDynamicColorsBySource, hasAssignedAgent } from '../../../pages/ruleBasedInbox/utils';
import { THREAD_CHANNEL_NAMES } from '../constants';
import ThreadLogViewModals, { useThreadLogViewModals } from '../inboxModals/threadLogViewModals';

interface ChatbotThread {
    flag_loader: boolean;
    flag_user: boolean;
    message: string;
    flagOpenMenu: boolean;
    flagOpenEmojiList: boolean;
    anchorEl: null | HTMLElement;
    chatbot_thread_id: number;
}

interface WebSocketResponse {
    response: {
        status: boolean;
        data: {
            chatbot_thread: ChatbotThread[];
        };
    };
    payload?: {
        url: string;
    };
}
interface chatUserThreadLogViewInterface {
    getSourceDetails: (ipAddress: string, chatbot_domain: string, source: string, phone_number: string | null, user_name: string | null, last_message_at: string, city: string, country: string, chatbot_user_input_token: number, chatbot_user_output_token: number, openai_assistant_id: string, openai_thread_id: string, waba_name: string | null, waba_phone_number: string | null, flag_azure?: boolean) => void,
    getSpecificChat: (chatbot_user_id: number | string, offSetValue: number, flag_debug: boolean, flagViewDetailedLog: boolean, forceRefresh?: boolean) => void,
    handleToggleManualOverride: (user_assignment_id: string, flag_delete_agent: boolean) => void,
    handleTakeoverConversation: (user_assignment_id: string, flag_delete_agent: boolean, flag_send_agent_activity_message: boolean) => void,
    getManualOverrideConfig: (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => string,
    flagRuleBasedInbox?: boolean,
    onArchiveToggle?: (threadId: string, flagArchive: boolean) => void,
    isLoadingChat?: boolean, // Show skeleton loader instead of round loader
    onRefreshThread?: () => void,
}

function ChatUserThreadLogView(props: chatUserThreadLogViewInterface) {
    // Destructure function props
    const { getSourceDetails, getSpecificChat, handleToggleManualOverride, getManualOverrideConfig,
        flagRuleBasedInbox, onArchiveToggle, isLoadingChat, onRefreshThread, handleTakeoverConversation 
    } = props;

    const { selectedThreadId, saveThreadScrollPosition, currentActiveChatbotThread, setCurrentActiveChatbotThread, 
        flagOpenThreadInfoBlock, chatClicked, flagDebug, checkAgentActivityMsgEnabled, flagRenderNewConversation
    } = useRuleBasedInboxStore(
        useShallow((state) => ({
            selectedThreadId: state.selectedThreadId,
            saveThreadScrollPosition: state.saveThreadScrollPosition,
            currentActiveChatbotThread: state.currentActiveChatbotThread,
            setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread,
            flagOpenThreadInfoBlock: state.flagOpenThreadInfoBlock,
            chatClicked: state.chatClicked,
            flagDebug: state.flagDebug,
            checkAgentActivityMsgEnabled: state.checkAgentActivityMsgEnabled,
            flagRenderNewConversation: state.flagRenderNewConversation
        }))
    )

    const { chatBotDetails } = useContext(ChatbotContext) as ChatbotContextPayload;
    const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;
    const { userDetail, webSocketObj, setWebSocketObj, setFlagLoading, handleError, vendorData } = useContext(UserContext) as UserContextPayload;

    const [searchParams, setSearchParams] = useSearchParams()
    const feedback_id = searchParams.get('feedback_id')

    const mobileScreen = useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)')
    const smallScreen = useMediaQuery<boolean>('(max-width : 1000px) and (min-width : 0px)')
    const laptopScreen = useMediaQuery<boolean>('(min-width: 1000px) and (max-width: 1600px)');
    const innerHeight = (mobileScreen && flagRuleBasedInbox) ? window.innerHeight : (window.innerHeight - 95);

    // Handlers and Props required for the Thread Log View modals
    const {
        getSteps,
        getSubChatbotExceutionLog,
        handleExecutionTimeLog,
        ...threadLogViewModalsProps
    } = useThreadLogViewModals();

    const targetRef = useRef(null);

    // ── Scroll-to-bottom button (rule-based inbox only) ──────────────────────
    // Shows a floating down-arrow when the user has scrolled above the bottom
    // of the chat; hidden when already at (or near) the bottom.
    const [showScrollDownBtn, setShowScrollDownBtn] = useState<boolean>(false);

    // Debounce timer for persisting scroll position while the user scrolls.
    const scrollSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Ref that always holds the latest selectedThreadId, updated synchronously
    // on every render.  This is intentionally a ref rather than a captured
    // closure value because useEffect cleanups run asynchronously (after
    // flushSync returns but before the next paint).  If a stale scroll listener
    // fires in that window — e.g. from keepScrollPinnedToBottom's initial
    // scrollToBottom() call — reading from this ref gives the correct *current*
    // thread ID rather than the one captured in a stale closure.
    const selectedThreadIdRef = useRef<string | null>(selectedThreadId);
    selectedThreadIdRef.current = selectedThreadId; // keep in sync every render

    const updateScrollDownBtnVisibility = useCallback(() => {
        const chatContainer = document.getElementById("chatUI");
        if (!chatContainer) {
            setShowScrollDownBtn(false);
            return;
        }
        // Consider "at bottom" if within 80 px of the end (tolerates rounding)
        const isAtBottom =
            chatContainer.scrollTop + chatContainer.clientHeight >=
            chatContainer.scrollHeight - 80;
        setShowScrollDownBtn(!isAtBottom);
    }, []);

    // Continuously persist scroll position as the user scrolls (debounced).
    // This is the primary save mechanism — it ensures the store always holds
    // the latest position, regardless of timing in handleThreadSelect.
    //
    // We use Infinity as the sentinel for "at bottom" (same convention as
    // handleThreadSelect) so that returning to this thread re-pins to the
    // bottom even if late-loading images have grown the content since we left.
    //
    // selectedThreadId is read from the ref (not the closure) so that even a
    // stale scroll listener always saves against the correct, current thread.
    const persistScrollPosition = useCallback(() => {
        if (!flagRuleBasedInbox || !selectedThreadIdRef.current) return;

        if (scrollSaveTimerRef.current) clearTimeout(scrollSaveTimerRef.current);

        scrollSaveTimerRef.current = setTimeout(() => {
            const threadId = selectedThreadIdRef.current;
            if (!threadId) return;
            const chatContainer = document.getElementById("chatUI");
            if (!chatContainer) return;
            // Always save the raw scrollTop — never the Infinity sentinel here.
            //
            // The Infinity sentinel ("was at bottom → re-pin on return") is only
            // reliable when scrollHeight is final (all images loaded).  This
            // debounce fires just 200 ms after a scroll event, which is often
            // before images have finished rendering — meaning scrollHeight can
            // still be artificially small, making scrollTop=0 appear to be "at
            // bottom" (false positive) even when the user was really at the top.
            //
            // Infinity is set exclusively by handleThreadSelect, which fires at
            // the moment of navigation — by then the user has been on the thread
            // long enough for images to load and scrollHeight to be correct.
            saveThreadScrollPosition(threadId, chatContainer.scrollTop);
        }, 200);
    // selectedThreadId intentionally omitted — we read from the ref instead so
    // this callback stays stable and never becomes a stale closure.
    }, [flagRuleBasedInbox, saveThreadScrollPosition]);

    // Attach / re-attach the scroll listener whenever the active thread changes.
    // The 150 ms delay lets the DOM settle after scroll-position restoration.
    useEffect(() => {
        if (!flagRuleBasedInbox || !currentActiveChatbotThread) {
            setShowScrollDownBtn(false);
            return;
        }

        let cleanup: (() => void) | undefined;

        const timerId = setTimeout(() => {
            const chatContainer = document.getElementById("chatUI");
            if (!chatContainer) return;

            const onScroll = () => {
                updateScrollDownBtnVisibility();
                persistScrollPosition();
            };

            chatContainer.addEventListener("scroll", onScroll, { passive: true });
            updateScrollDownBtnVisibility(); // reflect current position immediately

            cleanup = () => {
                chatContainer.removeEventListener("scroll", onScroll);
                // Flush any pending debounced save so it doesn't fire after the
                // thread is no longer the active one.
                if (scrollSaveTimerRef.current) {
                    clearTimeout(scrollSaveTimerRef.current);
                    scrollSaveTimerRef.current = null;
                }
            };
        }, 150);

        return () => {
            clearTimeout(timerId);
            cleanup?.();
        };
    }, [currentActiveChatbotThread, flagRuleBasedInbox, updateScrollDownBtnVisibility, persistScrollPosition]);

    const handleInitializeSocket = async () => {
        try {
            // const socketUrl = process.env.REACT_APP_SOCKET_URL;
            const socketUrl = getVendorField(vendorData, "socket api", process.env.REACT_APP_SOCKET_URL);
            let websocket: WebSocket | null = null;
            let pingInterval: NodeJS.Timeout | null = null;
            let intervalId: NodeJS.Timeout | null = null;
            let timeoutId: NodeJS.Timeout | null = null;
            const isMuted = false;

            const setChatBotDetail = (data: any) => {
                // Placeholder function for updating chatbot details
            };
            const setFlagInputDisable = (flag: boolean) => {
                // Placeholder function for input disable flag
            };

            const setOpenSnackbar = (data: any) => {
                // Placeholder function for showing snackbar
            };

            const playMessageReceived = () => {
                // Placeholder function for playing sound
            };

            const createWebSocket = () => {
                websocket = new WebSocket(socketUrl!);
                // @ts-ignore
                websocket['chatbot_user_id'] = currentActiveChatbotThread.chatbot_user_id
                setWebSocketObj(websocket)
                websocket.onopen = () => {

                    pingInterval = setInterval(() => {
                        if (websocket && websocket.readyState === WebSocket.OPEN) {
                            websocket.send("Keep Alive...");
                        }
                    }, 10000);
                };

                websocket.onmessage = async (event: MessageEvent) => {
                    const container = document.getElementById('chatUI');
                    const wsResponse: WebSocketResponse = JSON.parse(event.data);
                    const res: any = wsResponse.response;

                    if (wsResponse?.payload?.url === "chatbot/get-user-chats") {
                        if (res.status) {
                            if (res.status && res.data && res.data.length > 0) {
                                let chatbot_id = searchParams.get('chatbot_id')
                                let activeChats: any
                                let closedChats: any
                                let pagination: any
                                if (window.location.pathname.toLocaleLowerCase().includes('viewchatbot')) {
                                    activeChats = res.data[0]?.active_chats?.filter((chatbot: any) => chatbot.chatbot_id == Number(chatbot_id))?.flatMap((chatbot: any) => {
                                        const { chatbot_id, chatbot_name, chatbot_logo_url, chatbot_theme, chatbot_user_threads, openai_assistant_id } = chatbot
                                        return chatbot_user_threads?.filter((thread: any) => thread.chatbot_thread_data.length !== 0)?.map((thread: any) => ({ ...thread, chatbot_name, chatbot_logo_url, chatbot_theme, last_message_value: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].chatbot_thread_value, last_message_time: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].timestamp, flag_whatsapp: thread?.chatbot_user_route_source?.toLowerCase() == THREAD_CHANNEL_NAMES['WEBSITE']?.toLowerCase() ? false : true, chatbot_token: chatbot.chatbot_token, openai_assistant_id: openai_assistant_id, chatbot_id }))
                                    })
                                    closedChats = res.data[0]?.closed_chats?.chats?.filter((chatbot: any) => chatbot.chatbot_id == Number(chatbot_id))?.flatMap((chatbot: any) => {
                                        const { chatbot_id, chatbot_name, chatbot_logo_url, chatbot_theme, chatbot_user_threads, openai_assistant_id } = chatbot
                                        return chatbot_user_threads?.filter((thread: any) => thread.chatbot_thread_data.length !== 0)?.map((thread: any) => ({ ...thread, chatbot_name, chatbot_logo_url, chatbot_theme, last_message_value: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].chatbot_thread_value, last_message_time: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].timestamp, flag_whatsapp: thread?.chatbot_user_route_source?.toLowerCase() == THREAD_CHANNEL_NAMES['WEBSITE']?.toLowerCase() ? false : true, chatbot_token: chatbot.chatbot_token, openai_assistant_id: openai_assistant_id, chatbot_id }))
                                    })
                                    pagination = res.data[0]?.closed_chats?.pagination
                                } else {
                                    activeChats = res.data[0]?.active_chats?.flatMap((chatbot: any) => {
                                        const { chatbot_name, chatbot_logo_url, chatbot_theme, chatbot_user_threads, openai_assistant_id, chatbot_id } = chatbot
                                        return chatbot_user_threads.filter((thread: any) => thread.chatbot_thread_data.length !== 0)?.map((thread: any) => ({ ...thread, chatbot_name, chatbot_logo_url, chatbot_theme, last_message_value: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].chatbot_thread_value, last_message_time: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].timestamp, flag_whatsapp: thread?.chatbot_user_route_source?.toLowerCase() == THREAD_CHANNEL_NAMES['WEBSITE']?.toLowerCase() ? false : true, chatbot_token: chatbot.chatbot_token, openai_assistant_id: openai_assistant_id, chatbot_id }))
                                    })
                                    closedChats = res.data[0]?.closed_chats?.chats?.flatMap((chatbot: any) => {
                                        const { chatbot_id, chatbot_name, chatbot_logo_url, chatbot_theme, chatbot_user_threads, openai_assistant_id } = chatbot
                                        return chatbot_user_threads?.filter((thread: any) => thread.chatbot_thread_data.length !== 0)?.map((thread: any) => ({ ...thread, chatbot_name, chatbot_logo_url, chatbot_theme, last_message_value: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].chatbot_thread_value, last_message_time: thread.chatbot_thread_data[thread.chatbot_thread_data.length - 1].timestamp, flag_whatsapp: thread?.chatbot_user_route_source?.toLowerCase() == THREAD_CHANNEL_NAMES['WEBSITE']?.toLowerCase() ? false : true, chatbot_token: chatbot.chatbot_token, openai_assistant_id: openai_assistant_id, chatbot_id }))
                                    })
                                    pagination = res.data[0]?.closed_chats?.pagination
                                }
                                if (activeChats?.length > 0) {
                                    setSearchParams((prevParams: any) => {
                                        const newParams = new URLSearchParams(prevParams);
                                        newParams.set('chatbot_user_id', activeChats[0].chatbot_user_id);
                                        return newParams;
                                    });
                                    let activeChat = activeChats[0]
                                    let end_date = activeChat.chatbot_thread_data[activeChat.chatbot_thread_data.length - 1].timestamp ?? null
                                    getSourceDetails(
                                        activeChat.chatbot_user_ip_address,
                                        activeChat?.chatbot_domain ?? '',
                                        activeChat?.chatbot_user_route_source,
                                        activeChat.chatbot_phone_number,
                                        activeChat.chatbot_user_name,
                                        end_date,
                                        activeChat.chatbot_user_city,
                                        activeChat.chatbot_user_country,
                                        activeChat.chatbot_user_input_token,
                                        activeChat.chatbot_user_output_token,
                                        activeChat.openai_assistant_id,
                                        activeChat.openai_thread_id,
                                        null, null,
                                        activeChat?.flag_azure
                                    )
                                    setCurrentActiveChatbotThread(activeChats[0])
                                } else {
                                    if (closedChats?.length > 0) {
                                        setSearchParams((prevParams: URLSearchParams) => {
                                            const newParams = new URLSearchParams(prevParams);
                                            newParams.set('chatbot_user_id', closedChats[0].chatbot_user_id);
                                            return newParams;
                                        });
                                        setCurrentActiveChatbotThread(closedChats[0])
                                        let activeChat = closedChats[0]
                                        let end_date = activeChat.chatbot_thread_data[activeChat.chatbot_thread_data.length - 1].timestamp ?? null
                                        getSourceDetails(activeChat.chatbot_user_ip_address, 
                                            activeChat?.chatbot_domain ?? '',
                                            activeChat?.chatbot_user_route_source,
                                            activeChat.chatbot_phone_number,
                                            activeChat.chatbot_user_name,
                                            end_date,
                                            activeChat.chatbot_user_city,
                                            activeChat.chatbot_user_country,
                                            activeChat.chatbot_user_input_token,
                                            activeChat.chatbot_user_output_token,
                                            activeChat.openai_assistant_id,
                                            activeChat.openai_thread_id,
                                            null, null,
                                            activeChat?.flag_azure
                                        )
                                    }
                                }
                                setTimeout(() => {
                                    // let container = document.getElementById(chatbot_user_id.toString())
                                    if (container) {
                                        container.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
                                    }
                                }, 800)
                            }
                            if (container) {
                                setTimeout(() => {
                                    container.scrollTo({
                                        top: container.scrollHeight,
                                        behavior: 'smooth'
                                    });
                                }, 100);
                            }
                        } else {
                            setFlagInputDisable(false);
                            setChatBotDetail((prevState: any) => ({
                                ...prevState,
                                chatbot_thread: prevState.chatbot_thread?.map((thread: ChatbotThread) => ({
                                    ...thread,
                                    flag_loader: false
                                }))
                            }));
                            setOpenSnackbar((prevState: any) => ({ ...prevState, flagShowSnackbar: true }));
                        }
                    }
                };

                websocket.onclose = (event: CloseEvent) => {
                    clearInterval(pingInterval!);
                    pingInterval = null;

                    if (event.code !== 1000 && event.code !== 1008) {
                        createWebSocket();
                    }
                };
            };

            if (socketUrl) {
                createWebSocket();
            }

            return {
                status: true,
                webSocketObjTemp: websocket
            };
        } catch (err) {
            console.error("WebSocket initialization error", err);
        }
    };
    const waitForSocketConnection = (websocket: any, callback: any) => {
        setTimeout(() => {
            if (websocket?.readyState === WebSocket.OPEN) {
                if (callback) callback();
            } else {
                waitForSocketConnection(websocket, callback);
            }
        }, 5);
    };

    useEffect(() => {
        makeManualOverrideConnection()
    }, [currentActiveChatbotThread])

    function makeManualOverrideConnection() {
        if (currentActiveChatbotThread && webSocketObj?.chatbot_user_id !== currentActiveChatbotThread.chatbot_user_id) {
            if (("flag_manual_override" in currentActiveChatbotThread && currentActiveChatbotThread.flag_manual_override) || ("flag_manual" in currentActiveChatbotThread && currentActiveChatbotThread.flag_manual)) {
                handleInitializeSocket()
            }
        }
    }

    const ensureConversationTakenOver = useCallback(async (): Promise<boolean> => {
        if(flagRenderNewConversation) return true;
        if(!currentActiveChatbotThread) return false;

        const isAgentJoined = Boolean(currentActiveChatbotThread?.manual_agent_data?.[0]?.timestamp_joined);
        if (hasAssignedAgent(currentActiveChatbotThread)) {
            if (!isAgentJoined) {

                // Join Conversation (Update Status)
                const manualAgentId = currentActiveChatbotThread?.manual_agent_data?.[0]?.thread_manual_agent_id;
                const res = await agent.updateManualAgentStatus.put(manualAgentId, checkAgentActivityMsgEnabled(integrationListNewStructure));
                if(!res?.status){
                    handleError(res);
                    return false;
                }
            }
            return true;
        }
        
        // Assign Agent
        const payload = {
            thread_id: currentActiveChatbotThread?.chatbot_user_id,
            user_assignment_id: String(userDetail?.user_assignment_id),
            flag_send_agent_activity_message: checkAgentActivityMsgEnabled(integrationListNewStructure)
        }
        const response = await agent.addThreadManualAgentAPI.post(payload);
        if (!response.status) {
            handleError(response);
            return false;
        }
        return true;
    }, [flagRenderNewConversation, currentActiveChatbotThread, userDetail?.user_assignment_id, hasAssignedAgent, checkAgentActivityMsgEnabled, integrationListNewStructure, handleError]);

    const handleSendMessageManually = async (inputValue: string, flagMediaMessage: boolean, flagCannedResponseTemplate: boolean, flagTemplateMessage: boolean, selectedMediaSet: uploadedMedia[], sendTemplatePayload: sendTemplatePayload | null, cannedResponsePayload?: sendCannedResponsePayload) => {
        if (inputValue.trim() !== "" || flagMediaMessage || flagCannedResponseTemplate || flagTemplateMessage) {
            if (currentActiveChatbotThread || flagRenderNewConversation) {

                let response;
                // Manual override, canned response, and Basic template (with action_execution_data) use optimistic UI
                const usesOptimisticSend = !flagTemplateMessage || Boolean(sendTemplatePayload?.action_execution_data);
                const isAgentJoined = Boolean(currentActiveChatbotThread?.manual_agent_data?.[0]?.timestamp_joined);
                const needsAgentAssignment = usesOptimisticSend
                    && !flagRenderNewConversation
                    && Boolean(currentActiveChatbotThread && (!hasAssignedAgent(currentActiveChatbotThread) || !isAgentJoined));

                if (!usesOptimisticSend || needsAgentAssignment) {
                    setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
                }
                
                const ensureChatIsTakenover = await ensureConversationTakenOver(); // If user is not assigned to the conversation, Assign first.
                if (!ensureChatIsTakenover) {
                    setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
                    return;
                }

                if (needsAgentAssignment) {
                    setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
                }

                let pendingMessageKey: string[] | null = null;

                // Send Canned Response Messages
                if (flagCannedResponseTemplate && cannedResponsePayload) {
                    if (!flagRenderNewConversation) {
                        const now = new Date();
                        const pendingMessageCount = cannedResponsePayload?.canned_response_messages?.length || 0;
                        const pendingMessageKeys = Array.from({ length: pendingMessageCount }, () => crypto.randomUUID());
                        pendingMessageKey = pendingMessageKeys;

                        const optimisticMessages = buildOptimisticCannedResponseMessages({
                            cannedResponsePayload,
                            userDetail,
                            pendingMessageKeys,
                            timestamp: now.toISOString(),
                            stepTimestamp: now.getTime(),
                        });
                        appendPendingManualMessage(optimisticMessages, flagRuleBasedInbox);
                    }

                    response = await agent.sendCannedResponse.post(cannedResponsePayload);

                    if (response.status) {
                        const messageIds = getCannedResponseMessageIds(response?.data);
                        if (pendingMessageKey?.length && messageIds.length) {
                            reconcilePendingManualMessage(pendingMessageKey, messageIds, flagRuleBasedInbox);
                        }
                    } else if (pendingMessageKey?.length) {
                        removePendingManualMessage(pendingMessageKey, flagRuleBasedInbox);
                    }
                }
                else if (flagTemplateMessage && sendTemplatePayload) { // Send Template Messages
                    const { action_execution_data, ...templateApiPayload } = sendTemplatePayload;

                    if (!flagRenderNewConversation && action_execution_data) {
                        const now = new Date();
                        const pendingMessageKeys = [crypto.randomUUID()];
                        pendingMessageKey = pendingMessageKeys;
                        const optimisticMessages = buildOptimisticTemplateMessages({
                            actionExecutionData: action_execution_data,
                            userDetail,
                            pendingMessageKeys,
                            timestamp: now.toISOString(),
                            stepTimestamp: now.getTime(),
                        });
                        appendPendingManualMessage(optimisticMessages, flagRuleBasedInbox);
                    }

                    response = await agent.sendWhatsAppTemplate.post(templateApiPayload);

                    if (response.status) {
                        const messageId = getTemplateMessageId(response?.data);
                        if (pendingMessageKey?.length && messageId) {
                            reconcilePendingManualMessage(pendingMessageKey, [messageId], flagRuleBasedInbox);
                        }
                    } else if (pendingMessageKey?.length) {
                        removePendingManualMessage(pendingMessageKey, flagRuleBasedInbox);
                    }
                }
                else { // Send Media Messsag or Text Message
                    let payload;
                    const threadId = (currentActiveChatbotThread as currentActiveChatbotThreadTypeNew)?.thread_id ?? '';
                    const endUserPhoneNumber = (currentActiveChatbotThread as currentActiveChatbotThreadTypeNew)?.thread_metadata?.find((data: threadMetaData) => data?.metadata_type_name == "phone_number")?.metadata_value ?? "";

                    if (flagMediaMessage) {
                        payload = {
                            message_type: "media",
                            thread_id: threadId,
                            end_user_phone_number: endUserPhoneNumber,
                            media: selectedMediaSet || [],
                        }
                    } else {
                        payload = {
                            type: "text",
                            text: inputValue,
                            thread_id: threadId,
                            end_user_phone_number: endUserPhoneNumber
                        }
                    }

                    if (!flagRenderNewConversation) {
                        const now = new Date();
                        const pendingMessageCount = flagMediaMessage
                            ? (selectedMediaSet?.length || 0)
                            : 1;
                        const pendingMessageKeys = Array.from({ length: pendingMessageCount }, () => crypto.randomUUID());
                        pendingMessageKey = pendingMessageKeys;

                        const optimisticMessages = buildOptimisticManualMessage({
                            flagMediaMessage,
                            inputValue,
                            selectedMediaSet,
                            userDetail,
                            pendingMessageKeys,
                            timestamp: now.toISOString(),
                            stepTimestamp: now.getTime(),
                        });
                        appendPendingManualMessage(optimisticMessages, flagRuleBasedInbox);
                    }

                    response = await agent.manualOverrideNewStructure.post(payload);

                    if (response.status) {
                        const messageIds = response?.data?.threadExecutionLogIds ?? [];
                        if (pendingMessageKey?.length && messageIds.length) {
                            reconcilePendingManualMessage(pendingMessageKey, messageIds, flagRuleBasedInbox);
                        }
                    } else if (pendingMessageKey?.length) {
                        removePendingManualMessage(pendingMessageKey, flagRuleBasedInbox);
                    }
                }
                // setinputValue("")
                if (response.status) {

                    if(flagRenderNewConversation){
                        await getSpecificChat(response?.data?.threadId, 0, flagDebug, false)
                        const updatedThread = useRuleBasedInboxStore.getState().currentActiveChatbotThread;
                        const flagSendAgentActivityMsg = checkAgentActivityMsgEnabled(integrationListNewStructure);
                        await handleTakeoverConversation(String(userDetail.user_assignment_id), getManualOverrideConfig(updatedThread) == "Leave", flagSendAgentActivityMsg)
                    }

                    const container = document.getElementById('chatUI');
                    if (container) {
                        setTimeout(() => {
                            container.scrollTo({
                                top: container.scrollHeight,
                                behavior: 'smooth'
                            });
                        }, 100); // Delay of 100 milliseconds (adjust as needed)
                    }
                } else {
                    handleError(response);
                }
                if (!usesOptimisticSend) {
                    setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
                }
            }
        }
    };

    const getDynamicColorsByChatbotRouteSource = useCallback((source?: string) => {
        let sourceName = "";
        if(source) sourceName = source;
        else {
            if(currentActiveChatbotThread){
                sourceName = currentActiveChatbotThread?.chatbot_user_route_source;
            } else if (flagRenderNewConversation){
                sourceName = THREAD_CHANNEL_NAMES['WHATSAPP'];
            } else {
                sourceName = THREAD_CHANNEL_NAMES['WEBSITE'];
            }
        }

        return getDynamicColorsBySource(sourceName, currentActiveChatbotThread, chatBotDetails);
    }, [currentActiveChatbotThread, flagRenderNewConversation, chatBotDetails]);

    // Show skeleton when loading chat in Rule-Based Inbox
    if (flagRuleBasedInbox && isLoadingChat) {
        return <Grid item sx={{ width: smallScreen ? "100% !important" : "76% !important" }}>
            <ThreadViewSkeleton height={innerHeight} />
        </Grid>;
    }

    return (
        <>
            <Grid
                ref={targetRef}
                xs={smallScreen ? feedback_id ? 6 : 12 : undefined}
                item
                className={clsx(
                    styles.chatUserThreadLogViewContainer,
                    smallScreen
                        ? chatClicked
                            ? styles.chatUserThreadLogViewContainerSmallScreenVisible
                            : styles.chatUserThreadLogViewContainerSmallScreenHidden
                        : styles.chatUserThreadLogViewContainerDesktop,
                    feedback_id
                        ? flagOpenThreadInfoBlock
                            ? styles.chatUserThreadLogViewWidthFeedbackOpen
                            : styles.chatUserThreadLogViewWidthFeedbackClosed
                        : flagOpenThreadInfoBlock
                            ? laptopScreen ? styles.chatUserThreadLogViewWidthNoFeedbackClosed : styles.chatUserThreadLogViewWidthNoFeedbackOpen
                            : styles.chatUserThreadLogViewWidthNoFeedbackClosed
                )}
                sx={{
                    height: `${innerHeight}px !important`
                }}>
                <Grid container className={globalStyles.fullHeight} direction={"column"} gap={"0px"} flexWrap={"nowrap"}>
                    <ThreadLogHeader
                        getSpecificChat={getSpecificChat}
                        handleToggleManualOverride={handleToggleManualOverride}
                        getManualOverrideConfig={getManualOverrideConfig}
                        flagRuleBasedInbox={flagRuleBasedInbox}
                        onArchiveToggle={onArchiveToggle}
                        onRefreshThread={onRefreshThread}
                        getDynamicColorsByChatbotRouteSource={getDynamicColorsByChatbotRouteSource}
                    />

                    <Divider className={styles.dividerStyle} />

                    <Grid item
                        className={`${styles.chatLogViewMainGrid}`}
                        sx={{
                            backgroundColor: getDynamicColorsByChatbotRouteSource().chatbot_bg_color,
                            backgroundImage: `${(currentActiveChatbotThread && currentActiveChatbotThread?.chatbot_user_route_source?.toLowerCase() == "whatsapp" || flagRenderNewConversation) ? `url(${whatsAppDefaultBgImg})` : "none"}`
                        }}>
                        <Grid container className={clsx(globalStyles.fullHeight, globalStyles.fullwidth)} justifyContent={"space-between"} alignItems={"center"}>
                            <Grid item className={clsx(globalStyles.fullHeight, globalStyles.fullwidth)}>
                                <Box className={clsx(globalStyles.fullHeight, globalStyles.fullwidth)}>
                                    <Grid container className={clsx(globalStyles.fullHeight, globalStyles.fullwidth)}>
                                        <Grid item xs={12}
                                            className={clsx(globalStyles.fullHeight, globalStyles.fullwidth, styles.chatLogsViewInnerGrid)}>
                                            <Grid container className={clsx(globalStyles.fullHeight, globalStyles.fullwidth, globalStyles.overflowHidden)}>
                                                <Grid item xs={12} className={clsx(globalStyles.fullHeight, globalStyles.fullwidth)} sx={{ position: 'relative' }}>
                                                    <Grid container id="chatUI" direction={"column"} overflow={"auto"}
                                                        className={clsx(globalStyles.fullHeight, globalStyles.fullwidth, styles.chatbotChatMainSectionItem, globalStyles.relativePosition)}
                                                    >
                                                        <ThreadMessage
                                                            getDynamicColorsByChatbotRouteSource={getDynamicColorsByChatbotRouteSource}
                                                            getSteps={getSteps}
                                                            getSubChatbotExceutionLog={getSubChatbotExceutionLog}
                                                            handleExecutionTimeLog={handleExecutionTimeLog}
                                                            targetRef={targetRef}
                                                        />
                                                    </Grid>{/* end #chatUI */}

                                                    {/* ── Scroll-to-bottom button (rule-based inbox) ── */}
                                                    {flagRuleBasedInbox && showScrollDownBtn && (
                                                        <Box className={styles.scrollToBottomBtnWrapper}>
                                                            <Fab
                                                                size="small"
                                                                className={styles.scrollToBottomFab}
                                                                onClick={() => {
                                                                    const chatContainer = document.getElementById("chatUI");
                                                                    if (chatContainer) {
                                                                        chatContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: "smooth" });
                                                                    }
                                                                }}
                                                            >
                                                                <KeyboardArrowDownIcon className={styles.scrollToBottomIcon} />
                                                            </Fab>
                                                        </Box>
                                                    )}

                                                </Grid>{/* end relative wrapper xs={12} */}
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>

                    <ChatInput
                        handleSendMessageManually={handleSendMessageManually}
                        getManualOverrideConfig={getManualOverrideConfig}
                        getDynamicColorsByChatbotRouteSource={getDynamicColorsByChatbotRouteSource}
                      />
                </Grid>
            </Grid>

            {/* ── Thread Log View Popups  ── */}
            <ThreadLogViewModals
                handleSendMessageManually={handleSendMessageManually}
                {...threadLogViewModalsProps}
            />
        </>
    )
}

export default ChatUserThreadLogView
